// terminBlock.ts — pure Helfer für den Custom-Event-Renderer der Terminblöcke
// (Termine/Kalender SP2 „Grid-Lesbarkeit", Review 2026-07-05).
//
// Bewusst OHNE React/DOM, damit die Logik unter vitest (node-Env, nur *.test.ts)
// testbar bleibt: Ab wann bekommt ein Block die zweite Zeile (Zeit/Ort), wie
// wird sie formatiert, und was gilt als „mit Vorbehalt" (tentative, A6).
// Der eigentliche Renderer (TerminBlockInhalt) lebt in OutlookKalender.tsx.

import moment from "moment";
import { ensureMomentDe } from "../../utils/momentDe";
import type { CalendarMeta } from "./types";

// Zeitformatierung läuft über moment — Locale defensiv sicherstellen (SP1).
ensureMomentDe();

// ── Termin-Farben (SP4 hierher extrahiert aus OutlookKalender.tsx) ──────────
// EINE Quelle für die Art-Farben: der RBC-eventPropGetter (OutlookKalender)
// und das Peek-Popover (TerminPeekPopover, SP4) leiten die Akzentfarbe
// identisch ab — Duplikation wäre eine Drift-Falle.

/**
 * Farben je Termin-Art (TER-D5) — fester Hex-Satz für die react-big-calendar-
 * Inline-Styles (JS-Kontext, K5). Kundentermin=Gold, Austausch=Blau,
 * Sonstige=Grün, Block=Grau (geschützt), Abwesenheit=gedämpft je Typ.
 *
 * „Sonstige" (PO 21.09.2026) bekommt einen eigenen, klar unterscheidbaren Ton:
 * Grau ist als Block-Farbe vergeben, und ohne eigene Farbe wäre die neue Art im
 * Kalender von einem unklassifizierten Termin nicht zu unterscheiden.
 */
export const ART_FARBE = {
    kundentermin: "#f7a823",
    austausch: "#3a6ea5",
    sonstige: "#4b8f6a",
    block: "#a294a0",
    abwesenheitUrlaub: "#f7a823",
    abwesenheitKrank: "#c23b3b",
    abwesenheitHomeoffice: "#7d2169",
    abwesenheitNeutral: "#9a8f98",
} as const;

/**
 * Akzentfarbe (Marke) für die aktuelle-Zeit-Linie und Header-Details. Gold ist
 * der Markenakzent; bewusst kein Outlook-Blau, damit es zum kvp-Design passt.
 */
export const KALENDER_AKZENT = "#7d2169"; // Aubergine (REFINED.aub)

/**
 * Preset-Palette der individuellen Termin-Farbe (SP6/A10, Entscheidung 65) —
 * KMVS-intern (kein Outlook-Sync), kein Freifeld. MUSS 1:1 identisch bleiben
 * mit `TERMIN_FARBEN_PRESET` in
 * `apps/api/src/calendar/calendar-termin.controller.ts` (das Backend lehnt
 * alles außerhalb der Palette ab). Labels für Tooltip/aria-label des Pickers.
 */
export const TERMIN_FARBEN: { hex: string; label: string }[] = [
    { hex: "#c23b3b", label: "Rot" },
    { hex: "#d97a26", label: "Orange" },
    { hex: "#f7a823", label: "Gold" },
    { hex: "#5a8a3c", label: "Grün" },
    { hex: "#2e8a8a", label: "Petrol" },
    { hex: "#3a6ea5", label: "Blau" },
    { hex: "#7d2169", label: "Aubergine" },
    { hex: "#6e6e6e", label: "Grau" },
];

/** Anzeige-Label einer Preset-Farbe (Übersicht/Tooltip); unbekannt → Hex. */
export const terminFarbLabel = (hex: string): string =>
    TERMIN_FARBEN.find((f) => f.hex.toLowerCase() === hex.toLowerCase())?.label ?? hex;

/**
 * Hellen Hintergrund aus einer Hex-Akzentfarbe ableiten (Outlook-Kategorien-
 * Optik: heller Pillen-Hintergrund + farbiger linker Balken + dunkle Schrift).
 * Mischt die Farbe ~12 % über Weiß; robust für #rgb und #rrggbb.
 */
export const hellerHintergrund = (hex: string, alpha = 0.14): string => {
    let h = hex.replace("#", "");
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some((v) => Number.isNaN(v))) return "#f3f3f3";
    const mix = (c: number) => Math.round(c * alpha + 255 * (1 - alpha));
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

/**
 * Farbe aus dem Sidecar ableiten — Priorität (SP6/A10): individuelle `farbe`
 * schlägt die Art-Farbe; ohne beides → null (Kalender-Quellfarbe greift).
 * EINE Quelle für eventPropGetter (Grid) UND Peek-Popover.
 */
export const farbeAusMeta = (meta: CalendarMeta | null | undefined): string | null => {
    const individuell = (meta?.farbe ?? "").trim();
    if (individuell) return individuell;
    const art: string | undefined = meta?.terminArt ?? undefined;
    if (!art) return null;
    if (art === "kundentermin") return ART_FARBE.kundentermin;
    if (art === "austausch") return ART_FARBE.austausch;
    if (art === "sonstige") return ART_FARBE.sonstige;
    if (art === "block") return ART_FARBE.block;
    if (art === "abwesenheit") {
        if (meta?.abwesenheitTyp === "krank") return ART_FARBE.abwesenheitKrank;
        if (meta?.abwesenheitTyp === "homeoffice") return ART_FARBE.abwesenheitHomeoffice;
        if (meta?.abwesenheitTyp === "urlaub") return ART_FARBE.abwesenheitUrlaub;
        return ART_FARBE.abwesenheitNeutral;
    }
    return null;
};

/** Akzentfarbe eines View-Events: Farbe > Art-Farbe > Kalender-Quellfarbe > Marke. */
export const terminAkzentFarbe = (e: {
    meta?: CalendarMeta;
    source?: { color?: string };
}): string => farbeAusMeta(e.meta) || e.source?.color || KALENDER_AKZENT;

/**
 * Ab dieser Termin-Dauer (Minuten) zeigt der Block die zweite Zeile (Zeit/Ort)
 * — Default für die 15-min-Skala. Bei ~88px/Stunde (calendar.css) ist ein
 * 30-min-Block ~44px hoch — genug für Betreff + Zeitzeile; ein 15-min-Block
 * (~22px) bleibt einzeilig (nur Betreff). Die Schwelle hängt von der
 * Zeitskala ab (T-31, `rasterFuerSkala` in zeitskala.ts).
 */
export const ZEITZEILE_AB_MINUTEN = 30;

/** „Mit Vorbehalt" (Outlook tentative, A6) — Quelle: Graph `showAs`. */
export function istTentative(showAs: string | null | undefined): boolean {
    return showAs === "tentative";
}

/**
 * true, wenn der Block hoch genug für die Zeit/Ort-Zeile ist (Default ≥ 30 min;
 * bei gröberer Zeitskala übergibt der Kalender die passende Schwelle).
 */
export function zeigtZeitzeile(
    start: Date,
    end: Date,
    abMinuten: number = ZEITZEILE_AB_MINUTEN,
): boolean {
    return (end.getTime() - start.getTime()) / 60000 >= abMinuten;
}

/** Zweite Block-Zeile: „09:00 – 10:30" bzw. „09:00 – 10:30 · Raum 1". */
export function zeitOrtZeile(start: Date, end: Date, location?: string | null): string {
    const zeit = `${moment(start).format("HH:mm")} – ${moment(end).format("HH:mm")}`;
    const ort = (location ?? "").trim();
    return ort ? `${zeit} · ${ort}` : zeit;
}

/**
 * K9 — Tooltip-Zeile zum Vorgang am Termin. Genau EINE Quelle für Grid und
 * Peek. Ohne Leserecht am Vorgang liefert das Backend keinen Titel; dann steht
 * nur die Nummer da (die Id selbst ist kein Inhalt).
 */
export function vorgangHinweis(meta: CalendarMeta | null | undefined): string | null {
    const id = meta?.vorgangId ?? null;
    if (id == null) return null;
    const titel = (meta?.vorgangTitel ?? "").trim();
    return titel ? `Vorgang: ${titel}` : `Vorgang #${id}`;
}
